// src/hooks/useLiveBoard.ts
//
// Polling data hook for the reception live board (w7.u3, CF S6 "a board
// that needs no refresh"): mirrors `useArrivals`'s own poll/consecutive-
// failure/offline pattern rather than the on-demand `useAnalyticsFetch`
// every other Dirección page uses (this screen has no period picker and no
// manual refresh button — it just runs). Also tracks the previous poll's
// `board.needs_help` journey ids so the page can tell "a genuinely new row"
// from "the same row, still there" for the sound-on-new-signal rule (CF S6:
// "Sound only on a new Necesitan a alguien row").
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDataProvider } from 'react-admin';
import type { BoardResponse } from '@/types/analyticsV2';

const POLL_MS = 15_000;

export interface UseLiveBoardResult {
  data: BoardResponse | null;
  loading: boolean;
  error: boolean;
  consecutiveFailures: number;
  /** journey_ids present in `board.needs_help` for the first time this poll — empty on the very first load (nothing to compare against yet). */
  newNeedsHelpIds: string[];
  /** `Date.now()` of the last successful poll — lets a row's own elapsed timer keep ticking between polls instead of jumping every 15 s. */
  lastUpdatedAt: number | null;
  refresh: () => void;
}

const extractNeedsHelpIds = (data: BoardResponse | null): Set<string> => {
  const metric = data?.find((m) => m.id === 'board.needs_help');
  if (!metric || metric.absent || !Array.isArray(metric.value)) return new Set();
  const ids = new Set<string>();
  metric.value.forEach((row) => {
    const id = (row as Record<string, unknown>).journey_id;
    if (typeof id === 'string') ids.add(id);
  });
  return ids;
};

export const useLiveBoard = (property: string, enabled = true): UseLiveBoardResult => {
  const dataProvider = useDataProvider();
  const [data, setData] = useState<BoardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const [newNeedsHelpIds, setNewNeedsHelpIds] = useState<string[]>([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const knownNeedsHelpRef = useRef<Set<string> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchOnce = useCallback(async () => {
    if (!property) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const result = await (dataProvider as any).getAnalyticsBoard({ property }, controller.signal);
      const nextIds = extractNeedsHelpIds(result.data);
      if (knownNeedsHelpRef.current === null) {
        setNewNeedsHelpIds([]);
      } else {
        const previous = knownNeedsHelpRef.current;
        setNewNeedsHelpIds([...nextIds].filter((id) => !previous.has(id)));
      }
      knownNeedsHelpRef.current = nextIds;
      setData(result.data);
      setError(false);
      setConsecutiveFailures(0);
      setLastUpdatedAt(Date.now());
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      setError(true);
      setConsecutiveFailures((n) => n + 1);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProvider, property]);

  useEffect(() => {
    if (!enabled || !property) return undefined;
    setLoading(true);
    knownNeedsHelpRef.current = null;
    void fetchOnce();
    const handle = setInterval(() => void fetchOnce(), POLL_MS);
    return () => clearInterval(handle);
  }, [enabled, property, fetchOnce]);

  return { data, loading, error, consecutiveFailures, newNeedsHelpIds, lastUpdatedAt, refresh: fetchOnce };
};

export default useLiveBoard;
